import React, { useState } from 'react';
import { 
  Box, 
  VStack, 
  Heading, 
  useColorModeValue, 
  Flex,
  Text,
  useBreakpointValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Collapse,
  IconButton
} from '@chakra-ui/react';
import { serverTimestamp } from 'firebase/firestore';
import MediaUpload from '../MediaUpload';
import VideoUpload from './VideoUpload';
import ThreeDUpload from '../ThreeDUpload';
import { UploadSimple, Image, VideoCamera, Article, Cube, CaretDown, CaretUp } from '@phosphor-icons/react';
import ArticleUpload from './ArticleUpload';

export default function MediaGalleryUpload({ siteId, onUploadComplete, media, isMobile }) {
  const [tabIndex, setTabIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  
  // Responsive layout
  const iconSize = useBreakpointValue({ base: 18, md: 22 });
  const iconWeight = useBreakpointValue({ base: "regular", md: "light" });
  
  // Theme colors - Replaced useColorModeValue with direct semantic tokens below
  // const headingColor = useColorModeValue('ancient.stone', 'gray.400'); // Replaced -> text.muted
  // const uploadBorderColor = useColorModeValue('ancient.lightStone', 'gray.600'); // Replaced -> border.default
  // const uploadBgColor = useColorModeValue('ancient.subtle', 'gray.800'); // Replaced -> bg.accent
  const accentColor = 'ancient.terracotta'; // Assuming this is a valid theme color token
  // const sectionBg = useColorModeValue('ancient.papyrus', 'gray.900'); // Replaced -> bg.surface

  const handleMediaSubmit = async (mediaData, type) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // For 3D models, mediaData is already an array from ThreeDUpload
      if (type === '3d' && Array.isArray(mediaData)) {
        // Call the onUploadComplete function directly with the array
        const result = await onUploadComplete(mediaData);
        
        if (result?.warning) {
          toast({
            title: 'Success',
            description: result.warning,
            status: 'warning',
            duration: 5000,
            isClosable: true
          });
        } else {
          toast({
            title: 'Success',
            description: '3D model uploaded successfully',
            status: 'success',
            duration: 3000,
            isClosable: true
          });
        }
        return;
      }
      
      // For other media types (video, article)
      const mediaItem = {
        ...mediaData,
        siteId,
        uploadedBy: 'anonymous',
        path: mediaData.url, // Required for Firebase storage reference
        name: mediaData.title ? mediaData.title.replace(/\s+/g, '-').toLowerCase() : `${type}-${Date.now()}`, // Readable identifier
        size: 0, // Size not applicable for external content
        isMain: false,
        features: {},
        predictions: [],
        type: type,
        title: mediaData.title || `${type}-${Date.now()}`,
        description: mediaData.description || '',
        uploadedAt: serverTimestamp()
      };
      
      console.log('Submitting media item:', mediaItem);

      // Call the onUploadComplete function with properly formatted data
      const result = await onUploadComplete({
        items: [mediaItem]
      });

      if (result?.warning) {
        toast({
          title: 'Success',
          description: result.warning,
          status: 'warning',
          duration: 5000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Success',
          description: `${type} added successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      }

    } catch (error) {
      console.error('Error submitting media:', error);
      toast({
        title: 'Error',
        description: `Failed to add ${type}: ${error.message || 'Unknown error'}`,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box p={isMobile ? 2 : 4}>
      <VStack spacing={isMobile ? 2 : 4} align="stretch">
          <Tabs 
            variant="soft-rounded" 
            colorScheme="ancient"
            index={tabIndex} 
            onChange={setTabIndex}
            isLazy
            size={isMobile ? "sm" : "md"}
          >
            <TabList 
              overflowX="auto"
              overflowY="hidden"
              flexWrap={isMobile ? "nowrap" : "wrap"}
              sx={{
                scrollbarWidth: "none", // Firefox
                "&::-webkit-scrollbar": { // Chrome, Safari, Edge
                  display: "none"
                }
              }}
              pb={1} // Give some space for the scrollbar
              gap={isMobile ? 0 : 1}
            >
              <Tab 
                _selected={{ 
                  bg: 'ancient.terracotta', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
                color="text.muted"
                minW={isMobile ? "auto" : undefined}
                px={isMobile ? 2 : 3}
              >
                <Flex align="center" gap={2}>
                  <Image size={16} />
                  <Text textStyle="papyrus" fontSize={{ base: 'xs', md: 'sm' }}>Images</Text>
                </Flex>
              </Tab>
              <Tab 
                _selected={{ 
                  bg: 'ancient.terracotta', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
                color="text.muted"
                minW={isMobile ? "auto" : undefined}
                px={isMobile ? 2 : 3}
              >
                <Flex align="center" gap={2}>
                  <VideoCamera size={16} />
                  <Text textStyle="papyrus" fontSize={{ base: 'xs', md: 'sm' }}>Videos</Text>
                </Flex>
              </Tab>
              <Tab 
                _selected={{ 
                  bg: 'ancient.terracotta', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
                color="text.muted"
                minW={isMobile ? "auto" : undefined}
                px={isMobile ? 2 : 3}
              >
                <Flex align="center" gap={2}>
                  <Article size={16} />
                  <Text textStyle="papyrus" fontSize={{ base: 'xs', md: 'sm' }}>Articles</Text>
                </Flex>
              </Tab>
              <Tab 
                _selected={{ 
                  bg: 'ancient.terracotta', 
                  color: 'white',
                  fontWeight: 'bold'
                }}
                color="text.muted"
                minW={isMobile ? "auto" : undefined}
                px={isMobile ? 2 : 3}
              >
                <Flex align="center" gap={2}>
                  <Cube size={16} />
                  <Text textStyle="papyrus" fontSize={{ base: 'xs', md: 'sm' }}>3D Models</Text>
                </Flex>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={isMobile ? 2 : 4}>
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  color="text.muted"
                  mb={isMobile ? 2 : 4}
                  textStyle="papyrus"
                >
                  Upload images to document this site. Supported formats: JPG, PNG, GIF.
                </Text>
                <Box 
                  p={isMobile ? 2 : 4}
                  borderWidth="1px" 
                  borderStyle="dashed"
                  borderRadius="md"
                  borderColor="border.default"
                  bg="bg.accent"
                  _hover={{
                    borderColor: accentColor,
                    boxShadow: "sm"
                  }}
                >
                  <MediaUpload
                    siteId={siteId}
                    onUploadComplete={(uploadedItems) => {
                      // MediaUpload returns an array, but our parent expects the format used by other media types
                      if (Array.isArray(uploadedItems) && uploadedItems.length > 0) {
                        onUploadComplete({ items: uploadedItems });
                      } else {
                        onUploadComplete(uploadedItems);
                      }
                    }}
                    media={media}
                    isDisabled={isSubmitting}
                  />
                </Box>
              </TabPanel>
              <TabPanel p={isMobile ? 2 : 4}>
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  color="text.muted"
                  mb={isMobile ? 2 : 4}
                  textStyle="papyrus"
                >
                  Add video links from YouTube, Vimeo, or other supported platforms.
                </Text>
                <Box 
                  p={isMobile ? 2 : 4}
                  borderWidth="1px" 
                  borderStyle="dashed"
                  borderRadius="md"
                  borderColor="border.default"
                  bg="bg.accent"
                  _hover={{
                    borderColor: accentColor,
                    boxShadow: "sm"
                  }}
                >
                  <VideoUpload 
                    onVideoSubmit={(data) => handleMediaSubmit(data, 'video')}
                    isDisabled={isSubmitting}
                    isMobile={isMobile}
                  />
                </Box>
              </TabPanel>
              <TabPanel p={isMobile ? 2 : 4}>
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  color="text.muted"
                  mb={isMobile ? 2 : 4}
                  textStyle="papyrus"
                >
                  Add relevant article links about this site.
                </Text>
                <Box 
                  p={isMobile ? 2 : 4}
                  borderWidth="1px" 
                  borderStyle="dashed"
                  borderRadius="md"
                  borderColor="border.default"
                  bg="bg.accent"
                  _hover={{
                    borderColor: accentColor,
                    boxShadow: "sm"
                  }}
                >
                  <ArticleUpload 
                    onArticleSubmit={(data) => handleMediaSubmit(data, 'article')}
                    isDisabled={isSubmitting}
                    isMobile={isMobile}
                  />
                </Box>
              </TabPanel>
              <TabPanel p={isMobile ? 2 : 4}>
                <Text
                  fontSize={{ base: 'xs', md: 'sm' }}
                  color="text.muted"
                  mb={isMobile ? 2 : 4}
                  textStyle="papyrus"
                >
                  Upload 3D models, point clouds, and scans for interactive viewing. Supported formats: PLY, OBJ, STL, GLTF, GLB, IFC, LAS, LAZ.
                </Text>
                <Box 
                  p={isMobile ? 2 : 4}
                  borderWidth="1px" 
                  borderStyle="dashed"
                  borderRadius="md"
                  borderColor="border.default"
                  bg="bg.accent"
                  _hover={{
                    borderColor: accentColor,
                    boxShadow: "sm"
                  }}
                >
                  <ThreeDUpload
                    siteId={siteId}
                    onUploadComplete={(data) => handleMediaSubmit(data, '3d')}
                    media={media}
                    isDisabled={isSubmitting}
                  />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
      </VStack>
    </Box>
  );
}
